import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '@assets/css/common/style_main.css';
import '@assets/css/common/style_page_preset.css';
import '@assets/css/common/style_modal.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_save_status.css';

// API Services
import presetApi from '@services/presetApi.jsx';

// 썸네일 이미지 임포트 (실제 구현에서는 이미지 경로 조정 필요)
import startThumbnail from '@assets/images/thumbnails/start.png';
import angleThumbnail from '@assets/images/thumbnails/angle.png';
import layoutThumbnail from '@assets/images/thumbnails/layout.png';
import amountThumbnail from '@assets/images/thumbnails/amount.png';
import paymentThumbnail from '@assets/images/thumbnails/payment.png';
import paymentLoadingThumbnail from '@assets/images/thumbnails/payment_loading.png';
import shootingWaitThumbnail from '@assets/images/thumbnails/shooting_wait.png';
import shootingModeThumbnail from '@assets/images/thumbnails/shooting_mode.png';
import shootingLoadingThumbnail from '@assets/images/thumbnails/shooting_loading.png';
import selectModeThumbnail from '@assets/images/thumbnails/select_mode.png';
import printModeThumbnail from '@assets/images/thumbnails/print_mode.png';
import qrPageThumbnail from '@assets/images/thumbnails/qr_page.png';
import noticeThumbnail from '@assets/images/thumbnails/notice.png';

// 프리뷰 이미지 임포트 (실제 구현에서는 이미지 경로 조정 필요)
import startPreview from '@assets/images/preview/start_preview.png';
import anglePreview from '@assets/images/preview/angle_preview.png';
import layoutPreview from '@assets/images/preview/layout_preview.png';
import amountPreview from '@assets/images/preview/amount_preview.png';
import paymentPreview from '@assets/images/preview/payment_preview.png';
import paymentLoadingPreview from '@assets/images/preview/payment_loading_preview.png';
import shootingWaitPreview from '@assets/images/preview/shooting_wait_preview.png';
import shootingModePreview from '@assets/images/preview/shooting_mode_preview.png';
import shootingLoadingPreview from '@assets/images/preview/shooting_loading_preview.png';
import selectModePreview from '@assets/images/preview/select_mode_preview.png';
import printModePreview from '@assets/images/preview/print_mode_preview.png';
import qrPagePreview from '@assets/images/preview/qr_page_preview.png';
import noticePreview from '@assets/images/preview/notice_preview.png';

// 프리셋 컴포넌트 임포트
import PresetStartSetting from './components/PresetStartSetting';
import PresetAngleSetting from './components/PresetAngleSetting';
import PresetLayoutSetting from './components/PresetLayoutSetting';
import PresetAmountSetting from './components/PresetAmountSetting';
import PresetPaymentSetting from './components/PresetPaymentSetting';
import PresetPaymentLoadingSetting from './components/PresetPaymentLoadingSetting';
import PresetCaptureStandbySetting from './components/PresetCaptureStandbySetting';
import PresetCaptureSetting from './components/PresetCaptureSetting';
import PresetPostCaptureLoadingSetting from './components/PresetPostCaptureLoadingSetting';
import PresetPhotoSelectionModeSetting from './components/PresetPhotoSelectionModeSetting';
import PresetPrintModeSetting from './components/PresetPrintModeSetting';
import PresetQrPageSetting from './components/PresetQrPageSetting';
import PresetModalSetting from './components/PresetModalSetting';


const PagePreset = () => {
  // URL 파라미터 확인
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const presetId = searchParams.get('presetId');
  const currentPageParam = searchParams.get('page') || 'start';

  // 데이터 상태 관리
  const [preset, setPreset] = useState(location.state?.preset || null);
  const [presetPageMasters, setPresetPageMasters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 로컬 상태 관리
  const [currentPage, setCurrentPage] = useState(currentPageParam);
  const [currentTheme, setCurrentTheme] = useState({
    theme: searchParams.get('theme') || location.state?.preset?.presetName || location.state?.theme || '',
    author: searchParams.get('author') || location.state?.preset?.createdByName || location.state?.author || '',
    date: searchParams.get('date') || location.state?.preset?.startDatetime || location.state?.date || ''
  });

  // 프리셋 조회
  const  fetchPagePreset = async (presetId) => {
      setLoading(true);
      setError(null);

      try {
          const detailedPreset = await presetApi.getPreset(presetId);

          if (detailedPreset) {
              setPreset(detailedPreset);
              if (detailedPreset.presetPageMasters) {
                  setPresetPageMasters(detailedPreset.presetPageMasters);
              } else {
                  setPresetPageMasters([]);
              }
          }
      } catch (err) {
          console.error('Error fetching page preset : ' + err);
          setError('프리셋 정보를 가져오는데 실패했습니다.');
      } finally {
          setLoading(false);
      }
  }

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    //fetchPresetPageMasters(presetId);
    fetchPagePreset(presetId);
  }, [presetId]);
  
  // 컴포넌트 언마운트 시 타임아웃 정리
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);
  
  // location.state가 변경될 때 상태 업데이트

  // 저장 상태 관리
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  // 중복 클릭 방어를 위한 상태 및 ref 추가
  const saveTimeoutRef = useRef(null);
  const lastSaveTimeRef = useRef(0);
  const saveCountRef = useRef(0);
  const saveInProgressRef = useRef(false);
  
  // 저장 방어 설정값들
  const SAVE_COOLDOWN = 1000; // 1초 내 중복 저장 방지
  const MAX_SAVE_ATTEMPTS = 3; // 연속 클릭 최대 허용 횟수
  const COOLDOWN_RESET_TIME = 5000; // 5초 후 카운터 리셋
  const DOUBLE_CLICK_PREVENTION = 200; // 더블클릭 방지 최소 간격 (200ms)
  
  // 토글 상태 관리 (초기 상태와 현재 상태 분리)
  const [initialToggles, setInitialToggles] = useState({
    angle: false,
    layout: false,
    amount: false,
    payment: false,
    selectMode: false
  });
  
  const [currentToggles, setCurrentToggles] = useState({
    angle: false,
    layout: false,
    amount: false,
    payment: false,
    selectMode: false
  });
  
  // 변경된 데이터 추적
  const [hasChanges, setHasChanges] = useState(false);
  
  // Fallback용 기본 페이지 데이터 (먼저 정의)
  const presetPages = [
    { id: 'start', name: '시작화면' },
    { id: 'angle', name: '앵글선택' },
    { id: 'layout', name: '레이아웃' },
    { id: 'amount', name: '수량선택' },
    { id: 'payment', name: '결제' },
    { id: 'payment_loading', name: '결제 로딩' },
    { id: 'shooting_wait', name: '촬영대기' },
    { id: 'shooting_mode', name: '촬영모드' },
    { id: 'shooting_loading', name: '촬영 후 로딩' },
    { id: 'select_mode', name: '선택모드' },
    { id: 'print_mode', name: '인쇄모드/다운' },
    { id: 'qr_page', name: 'QR페이지' },
    { id: 'modal', name: 'Modal' }
  ];
  
  // 수정 가능한 페이지 타입들
  const editablePageTypes = ['angle', 'layout', 'amount', 'payment', 'select_mode'];
  
  // location.state가 변경될 때 상태 업데이트
  // useEffect(() => {
  //   if (location.state) {
  //     setCurrentPage(location.state.page || 'start');
  //
  //     // preset 정보가 있으면 업데이트
  //     if (location.state.preset) {
  //       setPreset(location.state.preset);
  //       setCurrentTheme({
  //         theme: location.state.preset.presetName,
  //         author: location.state.preset.createdByName,
  //         date: location.state.preset.startDatetime
  //       });
  //     } else {
  //       // 기존 방식 지원
  //       setCurrentTheme({
  //         theme: location.state.theme || '',
  //         author: location.state.author || '',
  //         date: location.state.date || ''
  //       });
  //     }
  //
  //     // presetPageMasters 정보가 있으면 업데이트
  //     if (location.state.presetPageMasters) {
  //       setPresetPageMasters(location.state.presetPageMasters);
  //     }
  //   }
  // }, [location.state]);
  
  // 썸네일과 프리뷰 이미지 매핑
  const getImageByPageType = (pageType) => {
    const imageMap = {
      start: { thumbnail: startThumbnail, preview: startPreview },
      angle: { thumbnail: angleThumbnail, preview: anglePreview },
      layout: { thumbnail: layoutThumbnail, preview: layoutPreview },
      amount: { thumbnail: amountThumbnail, preview: amountPreview },
      payment: { thumbnail: paymentThumbnail, preview: paymentPreview },
      payment_loading: { thumbnail: paymentLoadingThumbnail, preview: paymentLoadingPreview },
      shooting_wait: { thumbnail: shootingWaitThumbnail, preview: shootingWaitPreview },
      shooting_mode: { thumbnail: shootingModeThumbnail, preview: shootingModePreview },
      shooting_loading: { thumbnail: shootingLoadingThumbnail, preview: shootingLoadingPreview },
      select_mode: { thumbnail: selectModeThumbnail, preview: selectModePreview },
      print_mode: { thumbnail: printModeThumbnail, preview: printModePreview },
      qr_page: { thumbnail: qrPageThumbnail, preview: qrPagePreview },
      modal: { thumbnail: noticeThumbnail, preview: noticePreview }
    };
    
    return imageMap[pageType] || { thumbnail: noticeThumbnail, preview: noticePreview };
  };
  
  // 현재 표시할 페이지 목록 (useMemo로 최적화)
  const displayPages = useMemo(() => {
    if (presetPageMasters && presetPageMasters.length > 0) {
      return presetPageMasters.map(master => {
        const images = getImageByPageType(master.pageType);
        return {
          id: master.pageType,
          name: master.name,
          thumbnail: images.thumbnail,
          preview: images.preview,
          isActive: master.isActive,
          displayOrder: master.displayOrder,
          hasSaveData: master.hasSaveData,
          masterData: master
        };
      }).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      return presetPages.map(page => {
        const images = getImageByPageType(page.id);
        return {
          ...page,
          thumbnail: images.thumbnail,
          preview: images.preview,
          isActive: true,
          displayOrder: 0,
          hasSaveData: false,
          masterData: null
        };
      });
    }
  }, [presetPageMasters]);
  
  // 토글 상태를 presetPageMasters 데이터와 동기화
  useEffect(() => {
    if (presetPageMasters && presetPageMasters.length > 0) {
      const newToggles = {};
      editablePageTypes.forEach(pageType => {
        const master = presetPageMasters.find(pm => pm.pageType === pageType);
        const toggleKey = pageType;
        const isActiveValue = master ? master.isActive : false;
        newToggles[toggleKey] = isActiveValue;
      });
      
      // 초기 상태 설정 (처음 로드 시에만)
      if (Object.keys(initialToggles).length === 0 || Object.values(initialToggles).every(v => v === false)) {
        setInitialToggles(newToggles);
        setCurrentToggles(newToggles);
        setHasChanges(false);
      }
    }
  }, [presetPageMasters]);
  
  // 변경사항 감지
  useEffect(() => {
    const isChanged = Object.keys(currentToggles).some(
      key => currentToggles[key] !== initialToggles[key]
    );
    setHasChanges(isChanged);
  }, [currentToggles, initialToggles]);
  
  // 페이지 변경 핸들러
  const handlePageChange = (pageId) => {
    setCurrentPage(pageId);
    
    // URL 업데이트 (presetId 및 기타 정보 유지)
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', pageId);
    
    // 프리셋 정보도 URL에 유지
    if (preset) {
      newSearchParams.set('presetId', preset.id);
      newSearchParams.set('theme', preset.presetName || '');
      newSearchParams.set('author', preset.createdByName || '');
      newSearchParams.set('date', preset.startDatetime || '');
    }
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString()
    }, { replace: true });
  };
  
  // 뒤로가기 핸들러
  const handleGoBack = () => {
    navigate('/page-preset-management');
  };
  
  // 현재 선택된 페이지 정보
  const currentPageInfo = displayPages.find(page => page.id === currentPage) || displayPages[0] || {
    id: 'start',
    name: '시작화면',
    thumbnail: startThumbnail,
    preview: startPreview
  };
  
  // 토글 핸들러 (즉시 반응성 향상)
  const handleToggle = (key) => {
    const newValue = !currentToggles[key];
    const pageType = key === 'selectMode' ? 'select_mode' : key;
    
    setCurrentToggles(prev => ({
      ...prev,
      [key]: newValue
    }));
    
    // PresetPageMasters 상태도 즉시 업데이트 (UI 반응성 향상)
    setPresetPageMasters(prev => 
      prev.map(pm => {
        if (pm.pageType === pageType) {
          return { ...pm, isActive: newValue };
        }
        return pm;
      })
    );
  };
  
  // 저장 상태 라벨 렌더링 함수
  const renderSaveStatusLabel = (page) => {
    const hasSaveData = page.hasSaveData;
    const labelText = hasSaveData ? '확인완료' : '확인필요';
    const labelClass = hasSaveData ? 'save-status-completed' : 'save-status-required';
    
    return (
      <div className={`save-status-label ${labelClass}`}>
        {labelText}
      </div>
    );
  };
  
  // 설정 렌더링 함수
  const renderSettings = () => {
    const currentPageMaster = getCurrentPageMaster();

    // preset과 presetPageMaster 정보를 props로 전달
    const commonProps = {
      preset: preset,
      presetPageMaster: currentPageMaster,
      presetPageMasters: presetPageMasters,
      onUpdate: (updatedData) => {
        // 데이터 업데이트 로직
      }
    };
    
    switch(currentPage) {
      case 'start':
        return <PresetStartSetting {...commonProps} />;
      case 'angle':
        return <PresetAngleSetting {...commonProps} />;
      case 'layout':
        return <PresetLayoutSetting {...commonProps} />;
      case 'amount':
        return <PresetAmountSetting {...commonProps} />;
      case 'payment':
        return <PresetPaymentSetting {...commonProps} />;
      case 'payment_loading':
        return <PresetPaymentLoadingSetting {...commonProps} />;
      case 'shooting_wait':
        return <PresetCaptureStandbySetting {...commonProps} />;
      case 'shooting_mode':
        return <PresetCaptureSetting {...commonProps} />;
      case 'shooting_loading':
        return <PresetPostCaptureLoadingSetting {...commonProps} />;
      case 'select_mode':
        return <PresetPhotoSelectionModeSetting {...commonProps} />;
      case 'print_mode':
        return <PresetPrintModeSetting {...commonProps} />;
      case 'qr_page':
        return <PresetQrPageSetting {...commonProps} />;
      case 'modal':
        return <PresetModalSetting {...commonProps} />;
      default:
        return <div>해당 페이지의 설정이 준비 중입니다.</div>;
    }
  };
  
  // 현재 선택된 페이지의 PresetPageMaster 정보 찾기
  const getCurrentPageMaster = () => {
    return presetPageMasters.find(pm => pm.pageType === currentPage) || null;
  };
  
  // 현재 선택된 페이지 정보 (제거 - 이미 위에 정의됨)
  // const currentPageInfo = displayPages.find(page => page.id === currentPage) || displayPages[0];
  
  // 미리보기 버튼 핸들러
  const handlePreview = () => {
    alert('미리보기 기능이 준비 중입니다.');
  };

  // 중복 클릭 방어가 적용된 저장 함수
  const handlePageSave = useCallback(async () => {
    console.log('=== 저장 요청 시작 ===');
    
    // 최초 가드: 진행 중 플래그 체크 (가장 빠른 방어)
    if (saveInProgressRef.current) {
      console.warn('❌ 이미 저장 진행 중 - 완전 차단');
      return;
    }
    
    // 진행 중 플래그 즉시 설정
    saveInProgressRef.current = true;
    
    try {
      // 1. 기본 유효성 검사
      if (!preset) {
        alert('프리셋 정보가 없습니다.');
        return;
      }

      // 2. 현재 시간 체크
      const currentTime = Date.now();
      
      // 3. 이미 저장 중인 경우 방어
      if (isSaving) {
        console.warn('❌ 이미 저장 중입니다.');
        return;
      }
      
      // 4. 더블클릭 방지 체크
      const timeSinceLastSave = currentTime - lastSaveTimeRef.current;
      if (timeSinceLastSave < DOUBLE_CLICK_PREVENTION) {
        console.warn(`❌ 더블클릭 감지: ${timeSinceLastSave}ms < ${DOUBLE_CLICK_PREVENTION}ms`);
        return;
      }
      
      // 5. 연속 클릭 횟수 체크
      if (timeSinceLastSave < COOLDOWN_RESET_TIME) {
        saveCountRef.current += 1;
      } else {
        saveCountRef.current = 1; // 5초 이상 간격이면 카운트 리셋
      }
      
      if (saveCountRef.current > MAX_SAVE_ATTEMPTS) {
        alert('저장 요청이 너무 많습니다. 잠시 후 다시 시도해주세요.');
        return;
      }
      
      // 6. 저장 시작 시간 기록 및 상태 설정
      lastSaveTimeRef.current = currentTime;
      setIsSaving(true);
      
      console.log('✅ 저장 시작:', new Date().toISOString());
      
      // 기존 타임아웃 정리
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // 1. 현재 페이지의 개별 데이터 저장 (이벤트 발생)
      const pageEvent = new CustomEvent(`savePagePreset_${currentPage}`, {
        detail: { presetId: preset.id }
      });
      document.dispatchEvent(pageEvent);
      
      // 2. 변경된 토글 상태가 있으면 PresetPageMaster 업데이트
      if (hasChanges) {
        const changedToggles = getChangedToggles();
        if (changedToggles.length > 0) {
          await presetApi.updatePageMasterToggles(preset.id, changedToggles);
          
          // 성공적으로 저장된 후 로컬 상태 업데이트
          setInitialToggles({ ...currentToggles });
          setHasChanges(false);
          
          // PresetPageMasters 상태를 현재 토글 상태와 동기화
          setPresetPageMasters(prev => 
            prev.map(pm => {
              const toggleKey = pm.pageType === 'select_mode' ? 'selectMode' : pm.pageType;
              if (currentToggles.hasOwnProperty(toggleKey)) {
                return { ...pm, isActive: currentToggles[toggleKey] };
              }
              return pm;
            })
          );
        }
      }
      
      // 3. 저장 상태 업데이트를 위한 부분적 재조회 (토글 상태는 유지)
      try {
        const updatedPreset = await presetApi.getPreset(preset.id);
        if (updatedPreset && updatedPreset.presetPageMasters) {
          // 기존 토글 상태는 유지하면서 hasSaveData 같은 다른 속성만 업데이트
          setPresetPageMasters(prev => {
            return updatedPreset.presetPageMasters.map(newPm => {
              const existingPm = prev.find(pm => pm.pageType === newPm.pageType);
              if (existingPm) {
                // 토글 상태는 현재 로컬 상태 유지, 다른 속성은 서버에서 업데이트
                return {
                  ...newPm,
                  isActive: existingPm.isActive // 현재 토글 상태 유지
                };
              }
              return newPm;
            });
          });
        }
      } catch (reloadError) {
        console.warn('데이터 재조회 실패:', reloadError);
        // 재조회 실패해도 저장은 성공했으므로 계속 진행
      }
      
      console.log('✅ 저장 완료:', new Date().toISOString());
      alert('저장되었습니다.');
      
      // 저장 성공 시 카운터 리셋
      saveCountRef.current = 0;
      
    } catch (error) {
      console.error('❌ 저장 중 오류 발생:', error);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      console.log('🔄 저장 상태 정리 시작');
      
      // 즉시 진행 중 플래그 해제
      saveInProgressRef.current = false;
      
      // 지연된 상태 해제 (최소 500ms 대기)
      saveTimeoutRef.current = setTimeout(() => {
        setIsSaving(false);
        console.log('✅ 저장 상태 해제 완료:', new Date().toISOString());
      }, 500);
    }
  }, [preset, currentPage, hasChanges, currentToggles, isSaving]);

  // 저장 버튼 클릭 이벤트 핸들러 (추가 방어층)
  const handleSaveButtonClick = useCallback((e) => {
    console.log('🔘 버튼 클릭 감지');
    
    // 이벤트 중복 방지
    e.preventDefault();
    e.stopPropagation();
    
    // 최고 우선순위 가드
    if (saveInProgressRef.current) {
      console.warn('❌ 이미 진행 중 - 버튼 클릭 무시');
      return;
    }
    
    // 버튼이 비활성화된 상태라면 클릭 무시
    if (e.target.disabled || isSaving) {
      console.warn('❌ 저장 버튼 비활성 또는 저장 중 상태');
      return;
    }
    
    // 마지막 방어선: 더블클릭 방지를 위한 추가 체크
    const now = Date.now();
    if (now - lastSaveTimeRef.current < DOUBLE_CLICK_PREVENTION) {
      console.warn(`❌ 더블클릭 감지 - 버튼에서 차단: ${now - lastSaveTimeRef.current}ms`);
      return;
    }
    
    console.log('✅ 저장 함수 호출');
    handlePageSave();
  }, [handlePageSave, isSaving]);
  
  // 변경된 토글 상태 추출 함수
  const getChangedToggles = () => {
    return Object.keys(currentToggles)
      .filter(key => currentToggles[key] !== initialToggles[key])
      .map(key => {
        const pageType = key === 'selectMode' ? 'select_mode' : key;
        const master = presetPageMasters.find(pm => pm.pageType === pageType);
        return {
          id: master?.id,
          pageType: pageType,
          isActive: currentToggles[key]
        };
      })
      .filter(item => item.id); // id가 있는 것만 필터링
  };
  
  // 저장 버튼 핸들러 (일괄 저장)
  const handleSave = async () => {
    if (!preset) {
      alert('프리셋 정보가 없습니다.');
      return;
    }
    
    try {
      setIsSaving(true);
      setSaveError(null);
      
      if (!hasChanges) {
        // 변경사항이 없어도 현재 상태로 저장
        alert('현재 상태로 저장되었습니다.');
        setIsSaving(false);
        return;
      }
      
      // 변경된 토글 상태를 기반으로 PresetPageMaster 업데이트 데이터 준비
      const updatePromises = [];
      
      Object.keys(currentToggles).forEach(toggleKey => {
        if (currentToggles[toggleKey] !== initialToggles[toggleKey]) {
          const pageType = toggleKey === 'selectMode' ? 'select_mode' : toggleKey;
          const master = presetPageMasters.find(pm => pm.pageType === pageType);
          
          if (master) {
            // 기존 PresetPageMaster 업데이트
            const updateData = {
              ...master,
              isActive: currentToggles[toggleKey]
            };
            
            // TODO: 실제 API 호출 대신 임시 로직
            updatePromises.push(Promise.resolve(updateData));
          }
        }
      });
      
      // 모든 업데이트 실행
      const results = await Promise.all(updatePromises);
      
      // 성공 시 초기 상태 업데이트
      setInitialToggles({ ...currentToggles });
      setHasChanges(false);
      
      // PresetPageMasters 상태 동기화
      setPresetPageMasters(prev => 
        prev.map(pm => {
          const toggleKey = pm.pageType === 'select_mode' ? 'selectMode' : pm.pageType;
          return currentToggles.hasOwnProperty(toggleKey) 
            ? { ...pm, isActive: currentToggles[toggleKey] }
            : pm;
        })
      );
      
      alert('저장되었습니다.');
      
    } catch (error) {
      console.error('저장 중 오류 발생:', error);
      setSaveError('저장 중 오류가 발생했습니다.');
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <>
      {/* 로딩 표시 */}
      {(loading || isSaving) && (
        <div className="loading-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div className="loading" style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px'
          }}>{isSaving ? '저장 중...' : '로딩 중...'}</div>
        </div>
      )}
      
      {/* 에러 표시 */}
      {error && (
        <div className="error-overlay" style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#ff4444',
          color: 'white',
          padding: '15px',
          borderRadius: '8px',
          zIndex: 9999
        }}>
          <div className="error-message">{error}</div>
          <button 
            onClick={() => setError(null)}
            style={{
              marginLeft: '10px',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer'
            }}
          >×</button>
        </div>
      )}
      
      <div className="page-title-container">
        <a href="#" className="back-button" title="이전으로 돌아가기" onClick={(e) => { e.preventDefault(); handleGoBack(); }}>
          <i className="ri-arrow-left-line"></i>
        </a>
        <div className="title-theme-container">
          <h1 className="page-title">페이지 프리셋</h1>
          <h1 className="theme-name">{currentTheme.theme ? `ㅣ ${currentTheme.theme}` : ''}</h1>
        </div>
      </div>
      
      <div className="page-preset-container">
        <h3 className="section-title">페이지 선택</h3>
        {/* 통합된 프리셋 선택 컨테이너 */}
        <div className="preset-selection-container">
          {/* 페이지 선택 영역 */}
          <div className="preset-pages-row">
            {displayPages.map((page) => (
              <div
                key={page.id}
                className="preset-item-container" 
                data-preview={page.preview} 
                data-name={page.name}
                data-page={page.id}
                onClick={() => handlePageChange(page.id)}
              >
                {/* 프리셋 탭 */}
                <div className="preset-tab">{page.name}</div>
                
                {/* 썸네일 이미지 */}
                <div className="thumbnail-item">
                  <img src={page.thumbnail} alt={page.name} />
                </div>
                
                {/* 라디오 버튼 */}
                <div className="tab-radio">
                  <input 
                    type="radio" 
                    name="tab-select" 
                    checked={currentPage === page.id}
                    onChange={() => {}}
                  />
                </div>
                
                {/* 저장 상태 라벨 */}
                {renderSaveStatusLabel(page)}
              </div>
            ))}
          </div>

          {/* 토글 스위치 영역 */}
          <div className="preset-toggles-row">
            {displayPages.map((page, index) => {
              const toggleKey = page.id === 'select_mode' ? 'selectMode' : page.id;

              return (
                <div 
                  key={page.id}
                  className="preset-item-container" 
                  style={{
                    visibility: editablePageTypes.includes(page.id) ? 'visible' : 'hidden',
                    width: '150px'  // 모든 아이템의 너비를 동일하게 설정
                  }}
                >
                  <div className="toggle-switch-container-preset">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={page.isActive || false}
                        onChange={() => handleToggle(toggleKey)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <h3 className="section-title">프리셋 프리뷰</h3>
        {/* 16:9 이미지 프리뷰 컨테이너 */}
        <div className="preset-preview-container">
          <img src={currentPageInfo.preview} alt={currentPageInfo.name} id="preview-image" />
        </div>
        
        {/* 프리셋 설정 패널 */}
        <div className="preset-settings" id="preset-settings">
          {renderSettings()}
        </div>
        
        {/* 저장 버튼 - 개선된 버전 */}
        <div className="common-button-container">
          <button className="preset-preview-button" onClick={handlePreview}>미리보기</button>
          {/*<button className="preset-page-save-button" onClick={handlePageSave}>페이지 저장</button>*/}
          <button 
            className="common-button" 
            onClick={handleSaveButtonClick}
            disabled={isSaving}
            onDoubleClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.warn('더블클릭 이벤트 차단');
            }}
            style={{
              backgroundColor: isSaving ? '#ccc' : '',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.6 : 1,
              pointerEvents: isSaving ? 'none' : 'auto'
            }}
          >
            {isSaving ? '확인 중...' : '확인하기'}{hasChanges ? ' (변경사항 있음)' : ''}
          </button>
        </div>
      </div>
    </>
  );
};

export default PagePreset; 